import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Link,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../hooks';
import type { PendingEmailResponse } from '../api/auth';

export const SSOCallbackPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { handleSSOCallback, completeSSORegistration, isAuthenticated, error, isLoading } = useAuth();
  const [callbackError, setCallbackError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(true);

  // Pending email state (for Facebook users without email)
  const [pendingEmail, setPendingEmail] = useState<PendingEmailResponse | null>(null);
  const [email, setEmail] = useState('');
  const [tosAccepted, setTosAccepted] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const processedRef = useRef(false);

  useEffect(() => {
    if (processedRef.current) return;
    processedRef.current = true;

    const processCallback = async () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const errorParam = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      // Check for OAuth error response
      if (errorParam) {
        setCallbackError(errorDescription || errorParam);
        setIsProcessing(false);
        return;
      }

      // Check if this is an account linking flow (started from profile settings)
      const googleLinkState = sessionStorage.getItem('googleLinkState');
      const facebookLinkState = sessionStorage.getItem('facebookLinkState');
      const twitterLinkState = sessionStorage.getItem('twitterLinkState');
      if (state && (
        (googleLinkState && state === googleLinkState) ||
        (facebookLinkState && state === facebookLinkState) ||
        (twitterLinkState && state === twitterLinkState)
      )) {
        // This is an account linking flow - redirect to profile with the code
        // The profile page will handle the linking
        navigate(`/profile?code=${encodeURIComponent(code || '')}&state=${encodeURIComponent(state)}`);
        return;
      }

      // Get stored provider and state from session storage
      const storedState = sessionStorage.getItem('sso_state');
      const storedProvider = sessionStorage.getItem('sso_provider');

      // Validate state parameter to prevent CSRF attacks
      // We must have a stored state to validate against
      if (!storedState) {
        setCallbackError('Session expired. Please try signing in again.');
        setIsProcessing(false);
        return;
      }

      if (state !== storedState) {
        setCallbackError('Invalid state parameter. Please try signing in again.');
        setIsProcessing(false);
        return;
      }

      if (!code) {
        setCallbackError('No authorization code received. Please try signing in again.');
        setIsProcessing(false);
        return;
      }

      if (!storedProvider) {
        setCallbackError('No provider information found. Please try signing in again.');
        setIsProcessing(false);
        return;
      }

      try {
        const result = await handleSSOCallback(storedProvider, code, state || undefined);

        // Check if email is required (SSO user without email)
        if (result.type === 'pending_email') {
          setPendingEmail(result.pendingEmail);
          setIsProcessing(false);
          // Don't clean up session storage yet - we still need the redirect info
          return;
        }

        // Clean up session storage on success
        sessionStorage.removeItem('sso_state');
        sessionStorage.removeItem('sso_provider');
      } catch (err) {
        setCallbackError((err as Error).message || 'Failed to complete sign in');
      } finally {
        setIsProcessing(false);
      }
    };

    processCallback();
  }, [searchParams, handleSSOCallback, navigate]);

  useEffect(() => {
    if (isAuthenticated && !isProcessing && !pendingEmail) {
      // Get stored redirect destination or default to /boards
      const redirectTo = sessionStorage.getItem('sso_redirect') || '/boards';
      sessionStorage.removeItem('sso_redirect');
      sessionStorage.removeItem('sso_state');
      sessionStorage.removeItem('sso_provider');
      navigate(redirectTo);
    }
  }, [isAuthenticated, isProcessing, pendingEmail, navigate]);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError(null);

    if (!email) {
      setEmailError('Email is required');
      return;
    }

    if (!email.includes('@')) {
      setEmailError('Please enter a valid email address');
      return;
    }

    if (!tosAccepted) {
      setEmailError('You must accept the Terms of Service to continue');
      return;
    }

    if (!pendingEmail) {
      setEmailError('Session expired. Please try signing in again.');
      return;
    }

    setIsSubmitting(true);
    try {
      await completeSSORegistration(
        pendingEmail.pendingEmailToken,
        email,
        new Date().toISOString()
      );
      // Clean up session storage
      sessionStorage.removeItem('sso_state');
      sessionStorage.removeItem('sso_provider');
      // Clear pending email so the isAuthenticated navigation effect fires
      setPendingEmail(null);
    } catch (err) {
      setEmailError((err as Error).message || 'Failed to complete registration');
    } finally {
      setIsSubmitting(false);
    }
  };

  const displayError = callbackError || error;

  // Show email required form
  if (pendingEmail && !displayError) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <Card sx={{ maxWidth: 450, width: '100%' }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" component="h1" gutterBottom align="center">
              Email Required
            </Typography>

            <Alert severity="info" sx={{ mb: 3 }}>
              Your {pendingEmail.provider === 'facebook' ? 'Facebook' : pendingEmail.provider === 'twitter' ? 'Twitter' : pendingEmail.provider} account doesn't have an email address.
              Please provide an email to complete your registration.
            </Alert>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Welcome, <strong>{pendingEmail.displayName}</strong>! To create your SquareUp account, we need an email address
              where we can send important updates about your boards and account.
            </Typography>

            <form onSubmit={handleEmailSubmit}>
              <TextField
                label="Email Address"
                type="email"
                fullWidth
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isSubmitting}
                error={!!emailError}
                sx={{ mb: 2 }}
                autoFocus
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={tosAccepted}
                    onChange={(e) => setTosAccepted(e.target.checked)}
                    disabled={isSubmitting}
                  />
                }
                label={
                  <Typography variant="body2">
                    I agree to the{' '}
                    <Link component={RouterLink} to="/terms" target="_blank">
                      Terms of Service
                    </Link>{' '}
                    and{' '}
                    <Link component={RouterLink} to="/privacy" target="_blank">
                      Privacy Policy
                    </Link>
                  </Typography>
                }
                sx={{ mb: 2 }}
              />

              {emailError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {emailError}
                </Alert>
              )}

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={isSubmitting || isLoading}
                sx={{ mb: 2 }}
              >
                {isSubmitting ? <CircularProgress size={24} /> : 'Complete Registration'}
              </Button>

              <Typography variant="body2" align="center" color="text.secondary">
                <Link component={RouterLink} to="/login">
                  Cancel and return to sign in
                </Link>
              </Typography>
            </form>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (displayError) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <Card sx={{ maxWidth: 400, width: '100%' }}>
          <CardContent>
            <Typography variant="h5" component="h1" gutterBottom align="center">
              Sign In Failed
            </Typography>

            <Alert severity="error" sx={{ mb: 2 }}>
              {displayError}
            </Alert>

            <Typography variant="body2" align="center">
              <Link component={RouterLink} to="/login">
                Return to Sign In
              </Link>
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh',
      }}
    >
      <Card sx={{ maxWidth: 400, width: '100%' }}>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress size={48} sx={{ mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Completing sign in...
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};
